import React, { useEffect, useState } from "react";
import TypeApi from "../../api/TypeApi.js";
import SourceApi from "../../api/SourceApi.js";
import GroupApi from "../../api/GroupApi.js";
import UserApi from "../../api/UserApi.js";
import StateApi from "../../api/StateApi.js";
import LeadAssignApi from "../../api/LeadAssignApi.js";
import { showSuccess, showError } from "../../utils/toastUtils.js";
import useNumericInput from "../../hooks/useNumericInput.js";

function AddDetails({ onSuccess }) {
  useNumericInput();
  const [typeList, setTypeList] = useState([]);
  const [sourceList, setSourceList] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [managerList, setManagerList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [districtList, setDistrictList] = useState([]);

  const [form, setForm] = useState({
    typeId: "",
    sourceId: "",
    groupId: "",
    stateId: "",
    state: "",
    district: "",
    pincode: "",
    managerId: "",
  });

  useEffect(() => {
    loadMasters();
    loadStates();
    loadDistricts();
  }, []);

  /* ================== LOAD MASTERS ================== */
  const loadMasters = async () => {
    const [t, s, g, u] = await Promise.all([
      TypeApi.list(),
      SourceApi.list(),
      GroupApi.list(),
      UserApi.list(),
    ]);

    if (t.code === 200) setTypeList(t.data);
    if (s.code === 200) setSourceList(s.data);
    if (g.code === 200) setGroupList(g.data);

    // ✅ SAFE manager filter
    if (u.code === 200) {
      //console.log(u.data);
      const managers = u.data.filter(
        (x) => x.roleId && x.roleId?.role?.toLowerCase() === "manager",
      );

      setManagerList(managers);
    }
  };

  /* ================== STATES ================== */
  const loadStates = async () => {
    const res = await StateApi.getStates();
    if (res.code === 200) setStateList(res.data);
  };

  /* ================== DISTRICTS (BY STATE ID) ================== */
  const loadDistricts = async (stateId) => {
    if (!stateId) return;
    const res = await StateApi.getDistrictsByState(stateId);
    console.log(res);
    if (res.code === 200) setDistrictList(res.data);
    else setDistrictList([]);
  };

  /* ================== CHANGE ================== */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // ✅ PINCODE: only numbers + max 6
    if (name === "pincode") {
      const numericValue = value.replace(/\D/g, "").slice(0, 6);

      setForm((p) => ({
        ...p,
        pincode: numericValue,
      }));
      return;
    }

    if (name === "stateId") {
      const selectedState = stateList.find((s) => s._id === value);

      setForm((p) => ({
        ...p,
        stateId: value,
        state: selectedState?.name || "",
        district: "",
      }));

      loadDistricts(value);
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  /* ================== SUBMIT ================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.managerId) {
      return showError("Manager is required");
    }

    const payload = { ...form };
    delete payload.stateId;

    Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);

    try {
      const res = await LeadAssignApi.add(payload);

      if (res.code === 200) {
        showSuccess(res.message);
        document.querySelector("#add-leadassign-modal .btn-close")?.click();
        onSuccess();
      } else {
        showError(res.message);
      }
    } catch (err) {
      console.error("Add lead assign failed:", err);
      showError(err?.message || "Something went wrong");
    }
  };

  return (
    <div className="modal-dialog modal-lg">
      <div className="modal-content">
        <div className="modal-header">
          <h4 className="modal-title">Assign Lead</h4>
          <button className="btn-close" data-bs-dismiss="modal" />
        </div>

        <form onSubmit={handleSubmit} className="p-3 row">
          <Select
            col="4"
            label="Type"
            name="typeId"
            list={typeList}
            labelKey="type"
            value={form.typeId}
            onChange={handleChange}
          />
          <Select
            col="4"
            label="Source"
            name="sourceId"
            list={sourceList}
            labelKey="source"
            value={form.sourceId}
            onChange={handleChange}
          />
          <Select
            col="4"
            label="Group"
            name="groupId"
            list={groupList}
            labelKey="group"
            value={form.groupId}
            onChange={handleChange}
          />

          <Select
            col="4"
            label="State"
            name="stateId"
            list={stateList}
            labelKey="name"
            value={form.stateId}
            onChange={handleChange}
          />

          <Select
            col="4"
            label="District"
            name="district"
            list={districtList}
            labelKey="name"
            value={form.district}
            onChange={handleChange}
            disabled={!form.stateId}
          />

          <div className="col-md-4 mb-3">
            <label>Pincode</label>
            <input
              type="text"
              className="form-control only-numeric"
              data-maxlength="6"
              name="pincode"
              value={form.pincode}
              onChange={handleChange}
              placeholder="Enter Pincode"
            />
          </div>

          {/* ✅ Manager */}
          <Select
            col="12"
            label="Manager"
            name="managerId"
            list={managerList}
            labelKey="name"
            value={form.managerId}
            onChange={handleChange}
          />

          <div className="text-end">
            <button className="btn btn-primary">Assign</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ================== REUSABLE SELECT ================== */
const Select = ({
  col,
  label,
  name,
  value,
  onChange,
  list,
  labelKey,
  disabled,
}) => (
  <div className={`col-md-${col} mb-3`}>
    <label>{label}</label>
    <select
      className="form-select"
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      <option value="">Select {label}</option>
      {list.map((i) => (
        <option key={i._id} value={name === "district" ? i.name : i._id}>
          {i[labelKey]}
        </option>
      ))}
    </select>
  </div>
);

export default AddDetails;
